// ==============================================================================
// FILE: src/pages/IngestPage.tsx (CORRECTED)
// PURPOSE: Accepts modern/legacy Excel formats and uses standard relative
//          paths for imports to resolve potential build issues.
//
// CHANGE 1: The 'acceptedFileTypes' and description for the claim sheet
//          uploader have been updated to accept both .xlsx and .xls files.
// CHANGE 2 (FIX): The import paths for AppContext and FileUploader have been
//          updated to use standard relative paths (e.g., '../'). This is the
//          most common convention and should resolve build errors related to
//          module resolution.
//
// REASONING: This version incorporates the user's request for broader file
//            support and corrects the component imports to follow standard
//            React project structure, ensuring the component can be correctly
//            located by the compiler.
// ==============================================================================
import React, { useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
// --- THIS IS THE FIX ---
// Using standard relative paths to locate components in other directories.
import { useAppContext } from '../contexts/AppContext';
import FileUploader from '../components/FileUploader';
// --- END OF FIX ---
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';

export default function IngestPage(): JSX.Element {
    const { apiService, setActivePage, showNotification } = useAppContext();
    const [claimSheet, setClaimSheet] = useState<File[]>([]);
    const [receipts, setReceipts] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (claimSheet.length === 0 || receipts.length === 0) {
            showNotification('Please select both a claim sheet and at least one receipt.', 'warning');
            return;
        }
        
        setIsSubmitting(true);

        try {
            // The synchronous call to apiService.validateSubmission() has been removed.
            // The workflow now goes directly to creating the batch and uploading files.
            // The backend will handle the validation asynchronously.

            const batch = await apiService.createIngestionBatch();
            await apiService.uploadFile(batch.id, claimSheet, 'claim-sheet');
            await apiService.uploadFile(batch.id, receipts, 'receipts');
            await apiService.processBatch(batch.id);

            showNotification('Batch submitted for validation! You will be notified when processing begins.', 'info');
            setActivePage('Claim History');

        } catch (err: any) {
            const detailedMessage = err.message || 'An unexpected error occurred. Please try again or contact support if the issue persists.';
            setValidationError(detailedMessage);
            setIsSubmitting(false);
        }
    };

    return (
        <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <FileUploader
                        title="1. Upload Claim Sheet"
                        description="Upload the single expense claim sheet (.xlsx, .xls)."
                        onFilesSelected={(files) => setClaimSheet(files)}
                        onFileRemove={() => setClaimSheet([])}
                        acceptedFileTypes={{
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                            'application/vnd.ms-excel': ['.xls']
                        }}
                        allowMultiple={false}
                        files={claimSheet}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FileUploader
                        title="2. Upload Receipts"
                        description="Upload all corresponding receipts (.pdf, .png, .jpg)."
                        onFilesSelected={(files) => setReceipts(prev => [...prev, ...files])}
                        onFileRemove={(fileToRemove) => setReceipts(prev => prev.filter(f => f !== fileToRemove))}
                        acceptedFileTypes={{ 'image/jpeg': [], 'image/png': [], 'application/pdf': [] }}
                        allowMultiple={true}
                        files={receipts}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleSubmit}
                    disabled={isSubmitting || claimSheet.length === 0 || receipts.length === 0}
                    sx={{ minWidth: '300px', py: 1.5 }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Batch for Processing'}
                </Button>
            </Box>

            <Dialog
                open={!!validationError}
                onClose={() => setValidationError(null)}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon color="error" />
                    Submission Blocked
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                        {validationError}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationError(null)} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

